import { useMemo, useState } from 'react';

import type { VideaNotesAppointment } from '@/modules/videaNotes/helpers';

import type { TTreatmentResponse } from '../../claims/freeClaims/insuranceExport/types';
import type { ClinicalNote } from '../../../react-query/videa-notes/useExistingNotes';

type SortField = 'patient' | 'appointment' | 'treatments';
type SortDirection = 'asc' | 'desc';

export const useAmbientSorting = (
  appointmentsWithTreatmentsAndNotes: (VideaNotesAppointment & {
    treatments: TTreatmentResponse[];
    notes: ClinicalNote[];
  })[],
) => {
  // Sorting state
  const [sortField, setSortField] = useState<SortField>('appointment');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

  // Sort appointments with treatments and notes
  const sortedAppointments = useMemo(() => {
    const sorted = [...appointmentsWithTreatmentsAndNotes].sort((a, b) => {
      const direction = sortDirection === 'asc' ? 1 : -1;

      switch (sortField) {
        case 'patient': {
          const nameA =
            `${a.patient.firstName} ${a.patient.lastName}`.toLowerCase();
          const nameB =
            `${b.patient.firstName} ${b.patient.lastName}`.toLowerCase();
          return nameA.localeCompare(nameB) * direction;
        }
        case 'appointment': {
          const dateA = new Date(a.appointment.start).getTime();
          const dateB = new Date(b.appointment.start).getTime();
          return (dateA - dateB) * direction;
        }
        case 'treatments': {
          // Sort by number of treatments, then by appointment date
          const treatmentCountA = a.treatments?.length || 0;
          const treatmentCountB = b.treatments?.length || 0;

          if (treatmentCountA !== treatmentCountB) {
            return (treatmentCountA - treatmentCountB) * direction;
          }

          // If same number of treatments, sort by appointment date
          const dateA = new Date(a.appointment.start).getTime();
          const dateB = new Date(b.appointment.start).getTime();
          return (dateA - dateB) * direction;
        }
        default:
          return 0;
      }
    });

    return sorted;
  }, [appointmentsWithTreatmentsAndNotes, sortField, sortDirection]);

  const handleSort = (field: string, direction: SortDirection) => {
    setSortField(field as SortField);
    setSortDirection(direction);
  };

  return {
    sortedAppointments,
    sortField,
    sortDirection,
    handleSort,
  };
};
